import React, { useState, useEffect } from "react";
import { Plus, Trash2, Check, X, Settings, ToggleLeft, ToggleRight } from "lucide-react";
import { pb } from "../services/pocketbase";

export default function MotivosConfigTab({ user }) {
  const [motivos, setMotivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNome, setNewNome] = useState("");
  const [newObsObrigatoria, setNewObsObrigatoria] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editNome, setEditNome] = useState("");
  const [editObsObrigatoria, setEditObsObrigatoria] = useState(false);

  const loadMotivos = async () => {
    try {
      setLoading(true);
      const list = await pb.collection("estoque_suspensao_motivos").getFullList({
        sort: "nome",
      });
      setMotivos(list);
    } catch (e) {
      console.error("Erro ao carregar motivos de suspensão:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMotivos();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    const nomeClean = newNome.trim().toUpperCase();
    if (!nomeClean) return alert("Digite o nome do motivo.");

    try {
      await pb.collection("estoque_suspensao_motivos").create({
        nome: nomeClean,
        obs_obrigatoria: newObsObrigatoria,
        ativo: true,
      });
      alert("Motivo adicionado com sucesso!");
      setNewNome("");
      setNewObsObrigatoria(false);
      loadMotivos();
    } catch (err) {
      alert("Erro ao adicionar motivo: " + err.message);
    }
  };

  const handleToggleAtivo = async (motivo) => {
    try {
      await pb.collection("estoque_suspensao_motivos").update(motivo.id, {
        ativo: !motivo.ativo,
      });
      loadMotivos();
    } catch (err) {
      alert("Erro ao alterar status: " + err.message);
    }
  };

  const handleStartEdit = (motivo) => {
    setEditingId(motivo.id);
    setEditNome(motivo.nome);
    setEditObsObrigatoria(motivo.obs_obrigatoria);
  };

  const handleSaveEdit = async (id) => {
    const nomeClean = editNome.trim().toUpperCase();
    if (!nomeClean) return alert("Nome não pode ser vazio.");

    try {
      await pb.collection("estoque_suspensao_motivos").update(id, {
        nome: nomeClean,
        obs_obrigatoria: editObsObrigatoria,
      });
      setEditingId(null);
      loadMotivos();
    } catch (err) {
      alert("Erro ao atualizar motivo: " + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente excluir este motivo permanentemente?")) return;
    try {
      await pb.collection("estoque_suspensao_motivos").delete(id);
      loadMotivos();
    } catch (err) {
      alert("Erro ao excluir: " + err.message);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50">
      
      {/* HEADER */}
      <div className="bg-white border border-slate-200 p-5 rounded-xl shadow-sm flex items-center gap-3">
        <div className="p-3 bg-purple-100 rounded-lg text-purple-600">
          <Settings size={22} />
        </div>
        <div>
          <h2 className="text-base font-black text-slate-800 uppercase tracking-wider">Configurações de Motivos de Suspensão</h2>
          <p className="text-xs text-slate-500">Configure os motivos disponíveis para a oficina e escritório suspenderem itens da produção.</p>
        </div>
      </div>

      {/* RENDER GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* FORMULÁRIO DE CADASTRO */}
        <div className="bg-white border border-slate-200 p-5 rounded-xl shadow-sm h-fit space-y-4">
          <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider">Novo Motivo</h3>
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Nome do Motivo:</label>
              <input
                className="w-full border border-slate-300 rounded-lg p-2.5 text-xs outline-none focus:ring-2 focus:ring-purple-500 uppercase font-bold"
                placeholder="Ex: DEFEITO DE INJEÇÃO, QUEBRA..."
                value={newNome}
                onChange={(e) => setNewNome(e.target.value)}
                required
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="newObs"
                className="rounded text-purple-600 focus:ring-purple-500 h-4 w-4"
                checked={newObsObrigatoria}
                onChange={(e) => setNewObsObrigatoria(e.target.checked)}
              />
              <label htmlFor="newObs" className="text-xs font-bold text-slate-600 select-none">
                Exigir observação descritiva obrigatória
              </label>
            </div>
            <button
              type="submit"
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-2.5 rounded-lg text-xs shadow-sm transition-colors"
            >
              Adicionar Motivo
            </button>
          </form>
        </div>

        {/* LISTAGEM DE MOTIVOS */}
        <div className="bg-white border border-slate-200 p-5 rounded-xl shadow-sm lg:col-span-2 space-y-4">
          <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider">Motivos Cadastrados</h3>
          
          {loading ? (
            <div className="text-center py-6 text-slate-400 text-xs">Carregando motivos...</div>
          ) : motivos.length === 0 ? (
            <div className="text-center py-6 text-slate-400 text-xs">Nenhum motivo cadastrado.</div>
          ) : (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-400 font-bold">
                    <th className="p-3">Nome</th>
                    <th className="p-3">Obs. Obrigatória</th>
                    <th className="p-3">Status</th>
                    <th className="p-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {motivos.map((m) => {
                    const isEditing = editingId === m.id;
                    return (
                      <tr key={m.id} className="hover:bg-slate-50/50">
                        <td className="p-3 font-bold">
                          {isEditing ? (
                            <input
                              className="border border-slate-300 rounded p-1 text-xs uppercase w-full font-bold text-slate-800"
                              value={editNome}
                              onChange={(e) => setEditNome(e.target.value)}
                            />
                          ) : (
                            <span className="text-slate-800">{m.nome}</span>
                          )}
                        </td>
                        <td className="p-3">
                          {isEditing ? (
                            <input
                              type="checkbox"
                              className="rounded text-purple-600 focus:ring-purple-500 h-4 w-4"
                              checked={editObsObrigatoria}
                              onChange={(e) => setEditObsObrigatoria(e.target.checked)}
                            />
                          ) : (
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${m.obs_obrigatoria ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-500"}`}>
                              {m.obs_obrigatoria ? "Sim" : "Não"}
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          <button
                            onClick={() => handleToggleAtivo(m)}
                            className="flex items-center gap-1 hover:opacity-80 transition-opacity"
                          >
                            {m.ativo ? (
                              <ToggleRight size={22} className="text-purple-600" />
                            ) : (
                              <ToggleLeft size={22} className="text-slate-400" />
                            )}
                            <span className={`font-bold text-[10px] ${m.ativo ? "text-purple-600" : "text-slate-400"}`}>
                              {m.ativo ? "Ativo" : "Inativo"}
                            </span>
                          </button>
                        </td>
                        <td className="p-3 text-right">
                          {isEditing ? (
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => handleSaveEdit(m.id)}
                                className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                                title="Salvar"
                              >
                                <Check size={14} />
                              </button>
                              <button
                                onClick={() => setEditingId(null)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                title="Cancelar"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ) : (
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => handleStartEdit(m)}
                                className="text-slate-500 hover:text-slate-700 font-bold"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleDelete(m.id)}
                                className="text-red-500 hover:text-red-700 font-bold"
                              >
                                Excluir
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

    </div>
  );
}
